import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Scales, Plus, Trash, Copy, Eye } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function SteuerberaterVerwaltung() {
  const [users, setUsers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/stb-users").then(r => setUsers(r.data)).catch(()=>setUsers([]));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/stb-users", { name, email, password });
      toast.success("Steuerberater-Zugang angelegt");
      setName(""); setEmail(""); setPassword("");
      setShowForm(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Zugang wirklich löschen?")) return;
    try { await api.delete(`/stb-users/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error("Fehler"); }
  };

  const genPassword = () => {
    const chars = "abcdefghjkmnpqrstuvwxyzABCDEFGHJKMNPQRSTUVWXYZ23456789";
    let out = "";
    for (let i=0; i<12; i++) out += chars[Math.floor(Math.random()*chars.length)];
    setPassword(out);
  };

  return (
    <div className="space-y-6" data-testid="stb-verwaltung-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary flex items-center justify-center">
            <Scales size={22} weight="duotone" color="white"/>
          </div>
          <div>
            <h1 className="text-4xl font-bold tracking-tight" style={{fontFamily:"'Work Sans',sans-serif"}}>Steuerberater-Zugänge</h1>
            <p className="text-muted-foreground text-sm mt-1">Zugänge für Ihren Steuerberater verwalten · Read-Only Portal für Einnahmen, Ausgaben, PDFs</p>
          </div>
        </div>
        {!showForm && (
          <div className="flex gap-2">
            <Button onClick={()=>window.open("/steuerberater", "_blank")} variant="outline" className="rounded-xl h-10 px-4" data-testid="btn-portal-preview">
              <Eye size={16} weight="bold"/><span className="ml-1.5 font-semibold">Portal-Vorschau</span>
            </Button>
            <Button onClick={()=>setShowForm(true)} className="rounded-xl bg-primary text-white h-10 px-5" data-testid="btn-new-stb">
              <Plus size={16} weight="bold"/><span className="ml-1.5 font-semibold">Neuer Zugang</span>
            </Button>
          </div>
        )}
      </div>

      <div className="msb-card bg-slate-50 border-slate-200" data-testid="stb-info">
        <div className="flex items-start gap-3">
          <Eye size={20} weight="duotone" className="text-slate-600 flex-shrink-0 mt-0.5"/>
          <div className="text-sm">
            <div className="font-semibold text-slate-800">So sehen Sie, was Ihr Steuerberater sieht</div>
            <p className="text-slate-600 mt-1">Klicken Sie auf <strong>„Portal-Vorschau"</strong> oben rechts — Sie öffnen das Portal in einem neuen Tab mit denselben echten Zahlen, die Ihrem Steuerberater angezeigt werden (Einnahmen, Ausgaben, PDFs, Belege). Als Admin haben Sie ebenfalls Portalzugriff. Ihr Steuerberater sieht dagegen <em>nur</em> das Portal — keine Sidebar, keine Gäste-Details, keine Buchungen.</p>
          </div>
        </div>
      </div>

      {showForm && (
        <form onSubmit={submit} className="msb-card space-y-3" data-testid="stb-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Name *</Label>
              <Input value={name} onChange={(e)=>setName(e.target.value)} required className="h-11 rounded-lg mt-1" placeholder="z. B. StB Müller" data-testid="stb-name"/>
            </div>
            <div>
              <Label className="text-xs">E-Mail *</Label>
              <Input type="email" value={email} onChange={(e)=>setEmail(e.target.value)} required className="h-11 rounded-lg mt-1" placeholder="stb@kanzlei.de" data-testid="stb-email"/>
            </div>
          </div>
          <div>
            <Label className="text-xs">Passwort * (min. 6 Zeichen)</Label>
            <div className="flex gap-2 mt-1">
              <Input value={password} onChange={(e)=>setPassword(e.target.value)} required minLength={6} className="h-11 rounded-lg flex-1" placeholder="min. 6 Zeichen" data-testid="stb-password"/>
              <Button type="button" onClick={genPassword} variant="outline" className="rounded-lg" data-testid="stb-generate">
                Zufällig generieren
              </Button>
              {password && (
                <Button type="button" onClick={()=>{navigator.clipboard.writeText(password); toast.success("Kopiert");}} variant="outline" className="rounded-lg">
                  <Copy size={14} weight="bold"/>
                </Button>
              )}
            </div>
            <div className="text-[11px] text-muted-foreground mt-1">Teilen Sie E-Mail + Passwort sicher mit Ihrem Steuerberater — er meldet sich unter <code className="bg-slate-100 px-1 rounded">/login</code> an und wird automatisch ins Portal geleitet.</div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={()=>{setShowForm(false);}} disabled={busy} className="rounded-lg">Abbrechen</Button>
            <Button type="submit" disabled={busy} className="rounded-lg bg-primary text-white" data-testid="stb-save">Anlegen</Button>
          </div>
        </form>
      )}

      <div className="msb-card p-0 overflow-hidden">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-bold" style={{fontFamily:"'Work Sans',sans-serif"}}>Bestehende Zugänge</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/60">
              <tr className="text-xs uppercase tracking-widest text-muted-foreground">
                <th className="text-left p-3">Name</th>
                <th className="text-left p-3">E-Mail</th>
                <th className="text-left p-3">Angelegt</th>
                <th className="text-right p-3">Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Noch keine Steuerberater-Zugänge angelegt.</td></tr>}
              {users.map(u => (
                <tr key={u.id} className="border-t border-border" data-testid={`stb-row-${u.id}`}>
                  <td className="p-3 font-semibold">{u.name}</td>
                  <td className="p-3 font-mono text-xs">{u.email}</td>
                  <td className="p-3 text-xs text-muted-foreground">{u.created_at ? new Date(u.created_at).toLocaleDateString("de-DE") : "—"}</td>
                  <td className="p-3 text-right">
                    <button onClick={()=>del(u.id)} className="p-1.5 rounded hover:bg-red-100 text-red-600" title="Löschen" data-testid={`stb-del-${u.id}`}>
                      <Trash size={14} weight="bold"/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
